import { Injectable } from '@angular/core';
import { getToken, Messaging, onMessage } from '@angular/fire/messaging';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, EMPTY, Observable, tap } from 'rxjs';
import { MemberProfileHttpService } from '@core/entity/member-profile/service/member-profile.http.service';
import { PushNotificationCTAComponent, PushNotificationCTAData } from '@shared/push-notification-cta/push-notification-cta.component';
import { isStandaloneDisplay } from '@shared/utils/pwa-display-mode.util';
import { AesService } from './aes.service';

/** AIO-82875: what a push shows in-app when it arrives while the app is open. */
export interface ForegroundNotice {
  title: string;
  body: string;
}

/**
 * AIO-82875: FCM push registration (port of QPRO's FirebaseMessagingService, AIO-82875 branch).
 * `messaging` is null where Firebase could not start (no SSL on localhost dev, server render).
 */
@Injectable({
  providedIn: 'root',
})
export class FirebaseMessagingService {
  constructor(
    private messaging: Messaging,
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private aesService: AesService,
    private memberHttpService: MemberProfileHttpService
  ) { }

  /**
   * Register this device's push token. Resolves true only when the token was saved on the server.
   *
   * With promptIfNeeded=false this only registers a device whose permission is ALREADY granted. iOS
   * shows the permission prompt only for a request made during a tap, so the automatic call at
   * sign-in must not ask there - enableFromGesture() is the asking path.
   */
  async requestPermission(promptIfNeeded: boolean = true): Promise<boolean> {
    try {
      if (!this.messaging || !('serviceWorker' in navigator)) {
        throw new Error('Push messaging is not supported');
      }

      if (!promptIfNeeded && this.permissionState() !== 'granted') {
        return false;
      }

      const serviceWorkerRegistration = await this.registerServiceWorker();

      /* iOS refuses a second Notification.requestPermission() that is not inside a tap, even when permission
         was just granted by the first one ("Notification prompting can only be done from a user gesture"),
         so only ask when it is still undecided. */
      const permission = this.permissionState() === 'granted' ? 'granted' : await Notification.requestPermission();

      if (permission === 'granted') {
        try {
          const token = await getToken(this.messaging, { serviceWorkerRegistration });

          if (token) {
            /* No profile yet (storage cleared, or read before sign-in wrote it): skip, the next sign-in saves it */
            const member_account_id = this.memberHttpService.getMemberProfile()?.id;

            if (!member_account_id) {
              return false;
            }

            await this.saveTokenToBackend({
              member_account_id,
              token: this.aesService.encryptWithReqSignKey(token),
              /* The installed app keeps its own token beside the browser's */
              display_mode: isStandaloneDisplay() ? 'standalone' : 'browser',
            });

            return true;
          }
        } catch (tokenError) {
          console.error('Error getting token:', tokenError);
        }
      }
    } catch (err) {
      console.error('Failed to get permission:', err);
    }

    return false;
  }

  /** 'unsupported' where there is no Notification API - iOS Safari outside the installed app, in-app webviews. */
  permissionState(): NotificationPermission | 'unsupported' {
    return typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'unsupported';
  }

  /**
   * Ask for permission from a tap. Call it FIRST and synchronously in the tap handler.
   *
   * iOS and Safari show the prompt only for a request made during the tap, and any await before
   * Notification.requestPermission() spends the tap. So the prompt starts here; only then does the
   * usual registration run, whose own requestPermission() answers 'granted' at once.
   * Resolves with the answer, so the caller can tell a refusal ('denied') or a failed registration ('failed') from a yes.
   */
  enableFromGesture(): Promise<NotificationPermission | 'unsupported' | 'failed'> {
    if (this.permissionState() === 'unsupported') {
      return Promise.resolve('unsupported');
    }

    try {
      return Notification.requestPermission()
        .then(async (permission) => {
          /* Also when permission was already granted: the prompt answers at once, and this device still needs its token saved */
          /* 'failed': the member said yes but this device could not be registered (worker, FCM or server error) */
          if (permission === 'granted' && !(await this.requestPermission())) {
            return 'failed';
          }

          return permission;
        })
        .catch((err): NotificationPermission => {
          console.error('Failed to get permission:', err);
          return 'default';
        });
    } catch (err) {
      /* Safari before 15 has only the callback form, which returns no promise - never throw into the tap handler */
      console.error('Failed to get permission:', err);
      return Promise.resolve<NotificationPermission>('default');
    }
  }

  /**
   * The in-app notice for a message that arrives while the app is open - the browser shows nothing
   * then. The body is escaped: the notice renders it as HTML, and pushes also come from other
   * senders (FastTrack).
   */
  foregroundNotice(payload: any): ForegroundNotice | null {
    const source = payload?.notification ?? payload?.data ?? {};
    const title: string = source.title ?? '';
    const body: string = source.body ?? source.message ?? '';

    if (!title && !body) {
      return null;
    }

    return { title, body: escapeHtml(body) };
  }

  /** Shows the push snackbar: an in-app notice, or (with onConfirm) the iOS permission ask. */
  showNotice(data: PushNotificationCTAData, durationMs?: number): void {
    this.snackBar.openFromComponent(PushNotificationCTAComponent, {
      data,
      duration: durationMs,
      horizontalPosition: 'right',
      verticalPosition: 'top',
    });
  }

  listenToMessages(): Observable<any> {
    if (!this.messaging) {
      return EMPTY;
    }

    return new Observable((sub) =>
      onMessage(this.messaging, (msg) => sub.next(msg))
    );
  }

  /**
   * The messaging worker gets its OWN scope, never `/`: at `/` it would replace the registration of
   * `ngsw-worker.js`. With `<base href="/">` the scope is `/firebase-messaging`.
   *
   * It waits for THIS registration to be active, as the push subscription needs an active worker.
   * `navigator.serviceWorker.ready` waits for a worker on scope `/` instead, and there is none on
   * builds where ngsw is off, so it would never resolve.
   */
  private async registerServiceWorker(): Promise<ServiceWorkerRegistration> {
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: 'firebase-messaging' });

    await whenActive(registration);

    return registration;
  }

  private saveTokenToBackend(data: { member_account_id: number, token: string, display_mode: 'standalone' | 'browser' }): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.http.post('/fcm/token', data).pipe(
        tap(() => resolve()),
        catchError(err => {
          reject(err);
          throw err;
        })
      ).subscribe();
    });
  }
}

/** Resolves once the registration has an active worker; rejects if its new worker fails to install. */
function whenActive(registration: ServiceWorkerRegistration): Promise<void> {
  const worker = registration.installing || registration.waiting;

  if (registration.active || !worker) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    const onStateChange = () => {
      if (worker.state === 'activated') {
        worker.removeEventListener('statechange', onStateChange);
        resolve();
      } else if (worker.state === 'redundant') {
        worker.removeEventListener('statechange', onStateChange);
        reject(new Error('Firebase messaging service worker failed to install'));
      }
    };

    worker.addEventListener('statechange', onStateChange);
  });
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
